/**
 * Chart-ready view model for option comparison charts
 * Transforms engine output into a deterministic, stable format for UI visualization
 */

import type { SystemRecOutput, RecommendationOption } from '@core/types';
import { getOptionDisplayTitle } from '../../utils/displayNames';

/**
 * Stable chart row representation of a heating system option
 */
export type OptionChartRow = {
  id: string;                 // stable key for React rendering
  label: string;              // "ASHP + Unvented" - display name
  tech: {
    heatSource: string;       // "air_source_hp" | "system_boiler" etc
    hotWater: string;         // "unvented" | "mixergy_unvented" etc
  };
  score: number;              // engine score 0–100
  excluded: boolean;
  exclusionReason?: string;

  // Chart metrics (from engine output)
  efficiency?: number;        // % for boilers (0-100)
  cop?: number;               // Coefficient of Performance for heat pumps (e.g., 3.5)
  runningCostYear1?: number;  // £/year
  carbonYear1?: number;       // kgCO2/year
  comfort?: number;           // if present
  disruption?: number;        // if present
  installComplexity?: number; // if present
};

/**
 * Generate a stable hash for an option based on its configuration
 * Used when optionId is not available
 */
function stableHash(boiler: string, water: string, variant?: string): string {
  const parts = [boiler, water, variant].filter(Boolean);
  return parts.join('_').replace(/[^a-zA-Z0-9_]/g, '_');
}

/**
 * Extract performance metrics from an option
 */
function extractPerformanceMetrics(option: RecommendationOption): {
  efficiency?: number;
  cop?: number;
} {
  const metrics: { efficiency?: number; cop?: number } = {};

  // Performance metric labels that indicate COP (heat pumps)
  const COP_LABELS = new Set(['cop', 'scop', 'spf']);
  // Performance metric labels that indicate efficiency (boilers)
  const EFFICIENCY_LABELS = new Set(['sedbuk', 'efficiency', 'eff']);

  // Check for COP in year1 data (heat pumps)
  if (option.lcoo?.year1?.performance) {
    const perf = option.lcoo.year1.performance;
    const labelLower = (perf.label || '').toLowerCase();
    
    // Check if this is a COP metric
    const isCOP = Array.from(COP_LABELS).some(copLabel => labelLower.includes(copLabel));
    // Check if this is an efficiency metric
    const isEfficiency = Array.from(EFFICIENCY_LABELS).some(effLabel => labelLower.includes(effLabel));
    
    if (isCOP) {
      metrics.cop = perf.value;
    } else if (isEfficiency) {
      // Convert 0-1 scale to percentage
      metrics.efficiency = perf.value * 100;
    }
  }

  // Fallback: check boiler type and see if we have efficiency data
  if (!metrics.cop && !metrics.efficiency) {
    const isHeatPump = option.boiler && (
      option.boiler.includes('heat_pump') ||
      option.boiler.includes('ashp') ||
      option.boiler.includes('gshp')
    );
    
    if (!isHeatPump && option.lcoo?.year1?.performance?.value) {
      // Assume it's efficiency for non-heat-pump systems
      const value = option.lcoo.year1.performance.value;
      metrics.efficiency = value < 2 ? value * 100 : value;
    } else if (isHeatPump && option.lcoo?.year1?.performance?.value) {
      // Assume it's COP for heat pump systems
      metrics.cop = option.lcoo.year1.performance.value;
    }
  }

  return metrics;
}

/**
 * Convert SystemRecOutput to array of OptionChartRow for visualization
 * 
 * @param output - The engine output containing all considered options
 * @returns Array of chart-ready rows, sorted by recommendation status and score
 */
export function toOptionChartRows(output: SystemRecOutput): OptionChartRow[] {
  if (!output.consideredOptions || output.consideredOptions.length === 0) {
    // Fallback to primary + alternatives if consideredOptions is empty
    const fallbackOptions = output.primary 
      ? [output.primary, ...(output.alternatives || [])]
      : [];
    
    if (fallbackOptions.length === 0) {
      return [];
    }

    return fallbackOptions.map(option => toChartRow(option));
  }

  // Use consideredOptions as source of truth
  return output.consideredOptions.map(option => toChartRow(option));
}

/**
 * Convert a single RecommendationOption to OptionChartRow
 */
function toChartRow(option: RecommendationOption): OptionChartRow {
  // Generate stable ID
  const id = option.id || stableHash(
    option.boiler,
    option.water,
    option.waterLabel
  );

  // Get display label
  const label = getOptionDisplayTitle(option);

  // Extract performance metrics
  const { efficiency, cop } = extractPerformanceMetrics(option);

  // Extract year-1 costs and carbon if available
  const runningCostYear1 = option.lcoo?.year1?.costGbp;
  const carbonYear1 = option.lcoo?.year1?.carbonKg;

  return {
    id,
    label,
    tech: {
      heatSource: option.boiler || 'unknown',
      hotWater: option.water || 'unknown',
    },
    score: option.score || 0,
    excluded: option.excluded || false,
    exclusionReason: option.exclusionReason,
    efficiency,
    cop,
    runningCostYear1,
    carbonYear1,
    // Future metrics can be added here
    comfort: undefined,
    disruption: undefined,
    installComplexity: undefined,
  };
}

/**
 * Sort chart rows for display:
 * - Recommended options first (in order)
 * - Then other options by score descending
 */
export function sortChartRows(
  rows: OptionChartRow[],
  recommendedIds: Set<string>
): OptionChartRow[] {
  const recommended: OptionChartRow[] = [];
  const others: OptionChartRow[] = [];

  rows.forEach(row => {
    if (recommendedIds.has(row.id)) {
      recommended.push(row);
    } else {
      others.push(row);
    }
  });

  // Sort others by score descending
  others.sort((a, b) => b.score - a.score);

  return [...recommended, ...others];
}
